import { FC, forwardRef, ReactNode, useCallback, useEffect, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { LoadMoreButton } from '../../../../components/load-more-button/index';
import { PlaceholderBar } from '../../../../components/loading-skeleton/index';
import { ExpandedTableRowsPanel } from '../../../../components/table-animations/index';

const LOADING_CELL = 'px-4';
const LOADING_CELL_CLASS = ['w-[109px]', 'w-[127px]', 'w-[97px]'];

// Rows in a page panel sit in a table of their own, so the outer table's dividers do not reach them.
export const PAGE_ROW_CLASS = 'border-t border-[#F2F4F7] first:border-t-0';

/**
 * Rows in the shape of the table's own, pulsing where rows are on their way — the next page, or
 * the files being uploaded — as the first page's placeholder does.
 */
export const BrowserLoadingRows = ({
  columnCount = LOADING_CELL_CLASS.length + 2,
  count,
}: {
  // The table's columns: the name, the ones between, then the menu.
  columnCount?: number;
  count: number;
}) => (
  <>
    {Array.from({ length: count }).map((_, index) => (
      <tr aria-hidden className={twMerge('h-[75px] animate-pulse', PAGE_ROW_CLASS)} key={index}>
        {/* The bar may shrink: a fixed width would widen the table past its card and clip the
            menu column on a narrow screen. */}
        <td className={LOADING_CELL}>
          <span className="flex min-w-0 items-center gap-2">
            <PlaceholderBar className="size-8 shrink-0 rounded bg-gray-100" />
            <PlaceholderBar className="h-4 w-full min-w-0 max-w-40 rounded bg-gray-100" />
          </span>
        </td>
        {Array.from({ length: columnCount - 2 }, (_, index) => (
          <td className={twMerge(LOADING_CELL, LOADING_CELL_CLASS[index])} key={index}>
            <PlaceholderBar className="h-3 w-full max-w-12 rounded bg-gray-100" />
          </td>
        ))}
        <td className="w-12">
          <span className="flex justify-center">
            <PlaceholderBar className="size-4 rounded bg-gray-100" />
          </span>
        </td>
      </tr>
    ))}
  </>
);

type BrowserPagePanelProps = {
  children: ReactNode;
  // The columns of the table the panel sits in. The panel's row spans exactly these: a table with a
  // fixed layout counts every spanned column, and the one column left without a width (the name)
  // would share what remains with each phantom one and shrink to nothing.
  columnCount: number;
  // "Show less": the page closes, and reports once it is shut.
  isFolding: boolean;
  // The page is on its way: the panel holds placeholders, then the rows fade in over them.
  isLoading: boolean;
  onFolded?: () => void;
};

/**
 * One page past the first, revealed the way the cap table reveals its extra rows: the panel opens
 * from nothing to its rows' height and closes back to nothing. It opens on the frame after it
 * mounts, so even a page that arrives later slides in rather than appearing at once.
 */
export const BrowserPagePanel: FC<BrowserPagePanelProps> = ({
  children,
  columnCount,
  isFolding,
  isLoading,
  onFolded,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const frame = requestAnimationFrame(() => setIsOpen(true));

    return () => cancelAnimationFrame(frame);
  }, []);

  const onExpandedVisualChange = useCallback(
    (isExpandedVisual: boolean) => {
      if (!isExpandedVisual) onFolded?.();
    },
    [onFolded],
  );

  return (
    <ExpandedTableRowsPanel
      colSpan={columnCount}
      contentKey={isLoading ? 'loading' : 'loaded'}
      isExpanded={isOpen && !isFolding}
      onExpandedVisualChange={onExpandedVisualChange}
    >
      {children}
    </ExpandedTableRowsPanel>
  );
};

type BrowserPaginationBarProps = {
  count: number;
  isLoading: boolean;
  onLoadMore: () => void;
  onShowLess: () => void;
};

// The strip under the table holding the shared pill; with no next page it offers "Show less".
export const BrowserPaginationBar = forwardRef<HTMLDivElement, BrowserPaginationBarProps>(
  ({ count, isLoading, onLoadMore, onShowLess }, ref) => (
    <div className="flex justify-center border-t border-gray-100 bg-white py-4" ref={ref}>
      <LoadMoreButton
        count={count}
        isLoading={isLoading}
        onLoadMore={onLoadMore}
        onShowLess={onShowLess}
        showLess={count === 0}
      />
    </div>
  ),
);

BrowserPaginationBar.displayName = 'BrowserPaginationBar';
